/**
 * useOrganizations — React Query hooks for the Organization Admin feature.
 *
 * Provides queries and mutations for:
 *   - Registering / joining an organization
 *   - Fetching organization details and members
 *   - Browsing the course catalog and calculating prices
 *   - Purchasing course seats and managing assignments
 *   - Viewing member progress and exporting activity as CSV
 *   - Downloading member progress as PDF report
 *   - Downloading single course progress as PDF report
 *   - Downloading course completion certificate as PDF
 *   - Setting and fetching course pricing
 *   - Polling purchase status after Stripe redirect
 *   - Fetching aggregate dashboard data for org admin
 *   - Fetching the current user's org info for sidebar (admin or member)
 *   - Uploading an organization logo (admin only)
 *   - Sharing a course with an organization via code (Issue 157)
 *   - Listing and removing course shares
 *   - Disabling / enabling / deleting organization members (Issue 177)
 *
 * Follows the same patterns as useDeckLists.ts (query key factory, inline
 * queryFn calling the api layer, invalidation on mutation success).
 */

import {
  useQuery,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query';
import {
  registerOrganization,
  joinOrganization,
  getMyOrganization,
  updateMyOrganization,
  getMyOrgInfo,
  uploadOrgLogo,
  getOrganizationMembers,
  getOrganizationDashboard,
  getMembersSummary,
  getCatalog,
  calculatePrice,
  purchaseCourse,
  getMyPurchases,
  getPurchaseStatus,
  assignCourse,
  assignCourseBatch,
  unassignCourse,
  getAssignments,
  getMemberProgress,
  getMemberProgressGrouped,
  exportMemberActivity,
  downloadMemberReport,
  downloadSingleCourseReport,
  downloadCertificate,
  getCoursePricing,
  setCoursePricing,
  getCourseDetail,
  shareCourse,
  unshareCourse,
  listCourseShares,
  listSharedCourses,
  disableMember,
  enableMember,
  deleteMember,
} from '../api/organizations.api';
import type {
  RegisterOrganizationPayload,
  JoinOrganizationPayload,
  CalculatePricePayload,
  PurchaseCoursePayload,
  PurchaseCourseResponse,
  AssignCoursePayload,
  AssignCourseBatchPayload,
  SetCoursePricingPayload,
  CoursePricing,
  ShareCoursePayload,
  UpdateOrganizationPayload,
} from '../types';

// ============================================================
// Query Keys
// ============================================================

export const organizationKeys = {
  all: ['organizations'] as const,
  detail: () => [...organizationKeys.all, 'detail'] as const,
  myOrgInfo: () => [...organizationKeys.all, 'myOrgInfo'] as const,
  members: () => [...organizationKeys.all, 'members'] as const,
  membersSummary: () => [...organizationKeys.all, 'membersSummary'] as const,
  catalog: () => [...organizationKeys.all, 'catalog'] as const,
  purchases: () => [...organizationKeys.all, 'purchases'] as const,
  assignments: () => [...organizationKeys.all, 'assignments'] as const,
  memberProgress: (memberId: string) =>
    [...organizationKeys.all, 'memberProgress', memberId] as const,
  memberProgressGrouped: (memberId: string) =>
    [...organizationKeys.all, 'memberProgressGrouped', memberId] as const,
  pricing: (deckListId: string) =>
    [...organizationKeys.all, 'pricing', deckListId] as const,
  purchaseStatus: (purchaseId: string) =>
    [...organizationKeys.all, 'purchaseStatus', purchaseId] as const,
  courseDetail: (deckListId: string) =>
    [...organizationKeys.all, 'courseDetail', deckListId] as const,
  dashboard: () => [...organizationKeys.all, 'dashboard'] as const,
  courseShares: (deckListId: string) =>
    [...organizationKeys.all, 'courseShares', deckListId] as const,
  sharedCourses: () => [...organizationKeys.all, 'sharedCourses'] as const,
} as const;

// ============================================================
// Mutations — Organization registration & joining
// ============================================================

/** Register a new organization (mutation) */
export function useRegisterOrganization() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: RegisterOrganizationPayload) => registerOrganization(payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: organizationKeys.detail() });
    },
  });
}

/** Join an existing organization via code (mutation) */
export function useJoinOrganization() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: JoinOrganizationPayload) => joinOrganization(payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: organizationKeys.detail() });
      qc.invalidateQueries({ queryKey: organizationKeys.members() });
    },
  });
}

// ============================================================
// Queries — Organization & members
// ============================================================

/** Fetch the current user's organization (query) */
export function useMyOrganization() {
  return useQuery({
    queryKey: organizationKeys.detail(),
    queryFn: () => getMyOrganization(),
  });
}

/** Update organization details (mutation — admin only) */
export function useUpdateOrganization() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: UpdateOrganizationPayload) => updateMyOrganization(payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: organizationKeys.detail() });
      qc.invalidateQueries({ queryKey: organizationKeys.myOrgInfo() });
    },
  });
}

/** Fetch the current user's org info (admin or member) for sidebar logo (query) */
export function useMyOrgInfo() {
  return useQuery({
    queryKey: organizationKeys.myOrgInfo(),
    queryFn: () => getMyOrgInfo(),
  });
}

/** Fetch all members of the current organization (query) */
export function useOrganizationMembers() {
  return useQuery({
    queryKey: organizationKeys.members(),
    queryFn: () => getOrganizationMembers(),
  });
}

/** Fetch aggregate dashboard data for the organization (query) */
export function useOrganizationDashboard(options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: organizationKeys.dashboard(),
    queryFn: () => getOrganizationDashboard(),
    enabled: options?.enabled,
  });
}

/** Fetch members summary with aggregated progress (query) */
export function useMembersSummary() {
  return useQuery({
    queryKey: organizationKeys.membersSummary(),
    queryFn: () => getMembersSummary(),
  });
}

// ============================================================
// Mutations — Organization logo upload
// ============================================================

/** Upload organization logo (mutation — admin only) */
export function useUploadOrgLogo() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (file: File) => uploadOrgLogo(file),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: organizationKeys.detail() });
      qc.invalidateQueries({ queryKey: organizationKeys.myOrgInfo() });
    },
  });
}

// ============================================================
// Queries — Course catalog & purchases
// ============================================================

/** Fetch the course catalog (query) */
export function useCourseCatalog() {
  return useQuery({
    queryKey: organizationKeys.catalog(),
    queryFn: () => getCatalog(),
  });
}

/** Fetch detailed information about a specific course (query) */
export function useCourseDetail(deckListId: string) {
  return useQuery({
    queryKey: organizationKeys.courseDetail(deckListId),
    queryFn: () => getCourseDetail(deckListId),
    enabled: !!deckListId,
  });
}

/** Fetch all purchases for the organization (query) */
export function useMyPurchases() {
  return useQuery({
    queryKey: organizationKeys.purchases(),
    queryFn: () => getMyPurchases(),
  });
}

/** Poll purchase status after Stripe redirect (query with refetchInterval) */
export function usePurchaseStatus(purchaseId: string) {
  return useQuery({
    queryKey: organizationKeys.purchaseStatus(purchaseId),
    queryFn: () => getPurchaseStatus(purchaseId),
    enabled: !!purchaseId,
    refetchInterval: (query) => {
      const status = query.state.data?.status;
      if (status === 'completed' || status === 'expired' || status === 'cancelled' || status === 'refunded') {
        return false; // stop polling
      }
      return 2000; // poll every 2 seconds
    },
  });
}

// ============================================================
// Mutations — Pricing & purchasing
// ============================================================

/** Calculate price for a potential purchase (mutation) */
export function useCalculatePrice() {
  return useMutation({
    mutationFn: (payload: CalculatePricePayload) => calculatePrice(payload),
  });
}

/** Purchase course seats (mutation) */
export function usePurchaseCourse() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: PurchaseCoursePayload) => purchaseCourse(payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: organizationKeys.purchases() });
    },
  });
}

// ============================================================
// Queries — Course pricing
// ============================================================

/** Fetch pricing for a specific course / deck list (query) */
export function useCoursePricing(deckListId: string) {
  return useQuery({
    queryKey: organizationKeys.pricing(deckListId),
    queryFn: () => getCoursePricing(deckListId),
    enabled: !!deckListId,
  });
}

// ============================================================
// Mutations — Course pricing
// ============================================================

/** Set pricing for a course / deck list (mutation POST) */
export function useSetCoursePricing() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ deckListId, payload }: { deckListId: string; payload: SetCoursePricingPayload }) =>
      setCoursePricing(deckListId, payload),
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({ queryKey: organizationKeys.pricing(variables.deckListId) });
      qc.invalidateQueries({ queryKey: organizationKeys.catalog() });
    },
  });
}

// ============================================================
// Queries — Assignments
// ============================================================

/** Fetch all assignments with progress data (query) */
export function useAssignments() {
  return useQuery({
    queryKey: organizationKeys.assignments(),
    queryFn: () => getAssignments(),
  });
}

// ============================================================
// Mutations — Assign / unassign courses
// ============================================================

/** Assign a purchased course to a member (mutation) */
export function useAssignCourse() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: AssignCoursePayload) => assignCourse(payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: organizationKeys.assignments() });
      qc.invalidateQueries({ queryKey: organizationKeys.purchases() });
    },
  });
}

/** Assign a purchased course to multiple members in batch (Issue 159) */
export function useAssignCourseBatch() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: AssignCourseBatchPayload) => assignCourseBatch(payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: organizationKeys.assignments() });
      qc.invalidateQueries({ queryKey: organizationKeys.purchases() });
    },
  });
}

/** Remove an assignment (mutation) */
export function useUnassignCourse() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (assignmentId: string) => unassignCourse(assignmentId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: organizationKeys.assignments() });
      qc.invalidateQueries({ queryKey: organizationKeys.purchases() });
    },
  });
}

// ============================================================
// Mutations — Member management (Issue 177)
// ============================================================

/** Disable an organization member (mutation) */
export function useDisableMember() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (memberId: string) => disableMember(memberId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: organizationKeys.members() });
      qc.invalidateQueries({ queryKey: organizationKeys.membersSummary() });
      qc.invalidateQueries({ queryKey: organizationKeys.assignments() });
    },
  });
}

/** Enable a previously disabled organization member (mutation) */
export function useEnableMember() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (memberId: string) => enableMember(memberId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: organizationKeys.members() });
      qc.invalidateQueries({ queryKey: organizationKeys.membersSummary() });
      qc.invalidateQueries({ queryKey: organizationKeys.assignments() });
    },
  });
}

/** Delete an organization member and all associated data (mutation) */
export function useDeleteMember() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (memberId: string) => deleteMember(memberId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: organizationKeys.members() });
      qc.invalidateQueries({ queryKey: organizationKeys.membersSummary() });
      qc.invalidateQueries({ queryKey: organizationKeys.assignments() });
    },
  });
}

// ============================================================
// Queries — Member progress
// ============================================================

/** Fetch detailed progress for a specific member (query) */
export function useMemberProgress(memberId: string) {
  return useQuery({
    queryKey: organizationKeys.memberProgress(memberId),
    queryFn: () => getMemberProgress(memberId),
    enabled: !!memberId,
  });
}

/** Fetch grouped progress (by course) for a specific member (query) */
export function useMemberProgressGrouped(memberId: string) {
  return useQuery({
    queryKey: organizationKeys.memberProgressGrouped(memberId),
    queryFn: () => getMemberProgressGrouped(memberId),
    enabled: !!memberId,
  });
}

// ============================================================
// Mutations — Export & downloads
// ============================================================

/** Export organization activity as CSV (mutation — returns Blob) */
export function useExportMemberActivity() {
  return useMutation({
    mutationFn: () => exportMemberActivity(),
  });
}

/** Download member progress as PDF (mutation — returns Blob) */
export function useDownloadMemberReport() {
  return useMutation({
    mutationFn: (memberId: string) => downloadMemberReport(memberId),
  });
}

/** Download a single course progress as PDF (mutation — returns Blob) */
export function useDownloadSingleCourseReport() {
  return useMutation({
    mutationFn: ({ memberId, deckListId }: { memberId: string; deckListId: string }) =>
      downloadSingleCourseReport(memberId, deckListId),
  });
}

/** Download course completion certificate as PDF (mutation — returns Blob) */
export function useDownloadCertificate() {
  return useMutation({
    mutationFn: ({ memberId, deckListId }: { memberId: string; deckListId: string }) =>
      downloadCertificate(memberId, deckListId),
  });
}

// ============================================================
// Course Sharing (Issue 157)
// ============================================================

/** Share a course with an organization via code (mutation) */
export function useShareCourse() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ deckListId, payload }: { deckListId: string; payload: ShareCoursePayload }) =>
      shareCourse(deckListId, payload),
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({ queryKey: organizationKeys.courseShares(variables.deckListId) });
      qc.invalidateQueries({ queryKey: organizationKeys.purchases() });
      qc.invalidateQueries({ queryKey: organizationKeys.sharedCourses() });
    },
  });
}

/** Remove a course share (mutation) */
export function useUnshareCourse() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (shareId: string) => unshareCourse(shareId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: organizationKeys.all });
    },
  });
}

/** List all shares for a specific course (query) */
export function useCourseShares(deckListId: string) {
  return useQuery({
    queryKey: organizationKeys.courseShares(deckListId),
    queryFn: () => listCourseShares(deckListId),
    enabled: !!deckListId,
  });
}

/** List all shared courses for the organization (query) */
export function useSharedCourses() {
  return useQuery({
    queryKey: organizationKeys.sharedCourses(),
    queryFn: () => listSharedCourses(),
  });
}